// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button from "@insite/mobius/Button";
import { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button/Props";
import Icon, { IconPresentationProps, IconProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { color, mergeWithCss } from "@pacific/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    label?: string;
    iconProps?: {
        src: IconPresentationProps["src"];
        size: IconPresentationProps["size"];
    };
    extendedStyles?: IconButtonStyle;
}

type Props = OwnProps & ButtonProps;

export interface IconButtonStyle {
    button?: ButtonProps;
    label?: TypographyProps;
    iconWrapper?: IconPresentationProps;
}

export const iconButtonStyles: IconButtonStyle = {
    button: {
        variant: "tertiary",
        css: css`
            display: flex;
            align-items: center;
            min-height: 36px;
            padding: 0;
            width: fit-content;
            & .icon-container {
                width: 36px;
                height: 36px;
                padding: 8px;
                border-left: 1px solid ${color("primary")};
                & ${IconWrapper} {
                    color: ${color("primary")};
                }
            }
            &:hover {
                & .icon-container {
                    border-left: 1px solid ${color("common.ui8")};
                    & ${IconWrapper} {
                        color: ${color("common.ui8")};
                    }
                }
            }
        `,
    },
    label: {
        variant: "h6",
        css: css`
            padding: 0 12px;
            margin-bottom: 0;
        `,
    },
};

const IconButton: FC<Props> = ({ label, iconProps, extendedStyles, ...otherProps }) => {
    const [styles] = useState(() => mergeWithCss(iconButtonStyles, extendedStyles));

    return (
        <Button {...otherProps} {...styles.button} aria-label={label}>
            <Typography {...styles.label}>{label}</Typography>
            <StyledWrapper className="icon-container" {...styles.iconWrapper}>
                <Icon {...iconProps} />
            </StyledWrapper>
        </Button>
    );
};

export default IconButton;
